import { useEffect } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import Button, { LinkButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import ModalDialog, { ModalBody, ModalHeader } from '@atlaskit/modal-dialog';
import { Anchor, Box, xcss } from '@atlaskit/primitives';

import { urls } from 'src/urls';
import {
  publishScreenEvent,
  publishUiEvent,
} from 'src/utils/analytics/publish';

import bgImgUrl from './modal-bg.svg';
import heroImgUrl from './modal-hero-img.svg';

const scrollContainerStyles = xcss({
  width: '100%',
  height: '710px',
  overflowY: 'auto',
});

const headingStyles = xcss({
  position: 'relative',
  width: '100%',
  backgroundImage: `url(${bgImgUrl})`,
  backgroundRepeat: 'no-repeat',
});

const heroImgStyles = xcss({
  width: '564px',
  height: '257px',
  margin: 'auto',
  marginTop: 'space.negative.200', // to adjust for push-down from close button
  maxWidth: '100%',
});

const contentStyles = xcss({
  margin: 'auto',
  textAlign: 'center',
  width: '564px', // matches hero image width for visual consistency
});

const headerWrapperStyles = xcss({
  marginTop: 'space.500',
});

const blurbStyles = xcss({
  padding: 'space.200',
  marginTop: 'space.400',
  marginBottom: 'space.500',
});

const learnMoreStyles = xcss({
  fontWeight: 'font.weight.bold',
  textDecoration: 'none',
});

const remindMeWrapperStyles = xcss({
  marginTop: 'space.300',
  marginBottom: 'space.600',
});

const messages = defineMessages({
  headingText: {
    id: 'frontbucket.modals.unlinkedWorkspace.headingText',
    description: 'Text for heading of unlinked workspace modal',
    defaultMessage: 'Link this workspace to an Atlassian organization',
  },
  blurbText: {
    id: 'frontbucket.modals.unlinkedWorkspace.blurbText',
    description: 'Text for content of unlinked workspace modal',
    defaultMessage:
      'This is a critical step to getting this workspace ready for upcoming features like centralized user management and unified billing across your Atlassian products.',
  },
  learnMoreText: {
    id: 'frontbucket.modals.unlinkedWorkspace.learnMoreText',
    description: 'Text for learn more link of unlinked workspace modal',
    defaultMessage: 'Learn more',
  },
  linkWsText: {
    id: 'frontbucket.modals.unlinkedWorkspace.linkWsText',
    description: 'Text for link workspace button of unlinked workspace modal',
    defaultMessage: 'Link this workspace to an Atlassian organization',
  },
  remindMeText: {
    id: 'frontbucket.modals.unlinkedWorkspace.remindMeText',
    description: 'Text for remind me link of unlinked workspace modal',
    defaultMessage: 'Remind me in 2 days',
  },
});

const UnlinkedWorkspaceModal = ({ onClose }: { onClose: () => void }) => {
  const intl = useIntl();

  useEffect(() => {
    publishScreenEvent('unlinked-workspace-modal-shown');
  }, []);

  const onCloseModal = () => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'closeModal',
      source: 'unlinkedWorkspaceModal',
    });
    onClose();
  };

  const onLinkWorkspace = () => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'linkWorkspace',
      source: 'unlinkedWorkspaceModal',
    });
  };

  const onLearnMore = () => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: 'learnMore',
      source: 'unlinkedWorkspaceModal',
    });
  };

  const onDeferReminder = () => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'deferReminder',
      source: 'unlinkedWorkspaceModal',
    });
    onClose();
  };

  return (
    <ModalDialog
      width="x-large"
      height="auto"
      onClose={onCloseModal}
      shouldCloseOnEscapePress={false}
    >
      <Box xcss={scrollContainerStyles}>
        <Box xcss={headingStyles}>
          <ModalHeader hasCloseButton></ModalHeader>
          <Box xcss={heroImgStyles}>
            <img src={heroImgUrl} alt="Unlinked workspace background" />
          </Box>
        </Box>
        <ModalBody>
          <Box xcss={contentStyles}>
            <Box xcss={headerWrapperStyles}>
              <Heading size="xlarge">
                {intl.formatMessage(messages.headingText)}
              </Heading>
            </Box>

            <Box xcss={blurbStyles}>
              {intl.formatMessage(messages.blurbText)}
              <br />
              <Anchor
                href={urls.external.orgToWsLinkingSupportPage}
                xcss={learnMoreStyles}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onLearnMore}
              >
                {intl.formatMessage(messages.learnMoreText)}
              </Anchor>
            </Box>

            <Box>
              <LinkButton
                appearance="primary"
                href={urls.external.orgToWsLinking}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onLinkWorkspace}
              >
                {intl.formatMessage(messages.linkWsText)}
              </LinkButton>
              <Box xcss={remindMeWrapperStyles}>
                <Button appearance="subtle" onClick={onDeferReminder}>
                  {intl.formatMessage(messages.remindMeText)}
                </Button>
              </Box>
            </Box>
          </Box>
        </ModalBody>
      </Box>
    </ModalDialog>
  );
};

// eslint-disable-next-line import/no-default-export
export default UnlinkedWorkspaceModal;
